import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Chip,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Lưu ý: Nếu dùng MUI v5 thì import Grid từ '@mui/material/Grid' và đổi 'size' thành 'item xs'
import AuthContext from "flexbiz-core/components/auth/Context";
import { formatCurrency } from "flexbiz-core/utils"; 

// --- ICONS ---
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useHistory } from 'react-router-dom';

// --- COMPONENT KHUNG BÁO CÁO ---
import ReportFrame from 'flexbiz-core/components/ReportFrame'; // Đảm bảo đường dẫn này trỏ đúng tới file ReportFrame.jsx của bạn

export default function SalesReportPage() {
  const context = useContext(AuthContext);
  const history = useHistory();
  // ================= 1. STATE & API TẢI TRẠNG THÁI =================
  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await context.apis.asyncGetList(
          context.userInfo.token,
          "trangthai",
          {
            fields: "ma_trang_thai,ten_trang_thai",
            condition: { ma_ct: "NHADAT_MOIGIOI_BAN" }
          }
        );
        const data = response?.data || response || [];
        setStatusOptions(data);
      } catch (error) {
        console.error("Lỗi khi tải danh sách trạng thái:", error);
      }
    };
    fetchStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ================= 2. CẤU HÌNH BỘ LỌC =================
  const filterFields = [
    { 
      name: 'trang_thai', 
      label: 'Trạng thái giao dịch', 
      type: 'select', 
      width: 12,
      options: [
        { value: '', label: 'Tất cả trạng thái' },
        ...statusOptions.map(s => ({ value: s.ma_trang_thai, label: s.ten_trang_thai }))
      ]
    },
    { name: 'tu_ngay', label: 'Từ ngày', type: 'date', width: 6 },
    { name: 'den_ngay', label: 'Đến ngày', type: 'date', width: 6 }
  ];

  // ================= 3. ĐIỀU KIỆN MẶC ĐỊNH GỬI API =================
  // Những tham số ẩn này sẽ luôn được gửi kèm mỗi khi ReportFrame gọi API
  const defaultCondition = {
    user_moi_gioi: context.userInfo?.email, 
    skip: 0
  };

  const formatDate = (isoString) => {
    if (!isoString) return '---';
    return new Date(isoString).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // ================= 4. HÀM VẼ GIAO DIỆN CUSTOM =================
  const renderCustomReport = (data, { onLoadMore, isFetchingMore, currentLimit }) => {
    // Trích xuất dữ liệu từ API trả về
    const summary = data?.summary || { 
      tong_so_lo: 0, 
      tong_doanh_thu: 0, 
      tong_hoa_hong_truc_tiep: 0, 
      tong_hoa_hong_gian_tiep: 0, 
      tong_thu_nhap: 0 
    };
    const list = data?.list || [];

    // Kiểm tra xem danh sách trả về đã chạm ngưỡng limit chưa để hiện nút Tải thêm
    const hasMore = list.length >= currentLimit;

    return (
      <Box sx={{ pb: 4 }}>
        
        {/* --- WIDGET TỔNG QUAN (SUMMARY) --- */}
        <Card sx={{ mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)', color: 'white', boxShadow: '0 8px 16px rgba(76, 175, 80, 0.25)' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
              Tổng thu nhập ước tính
            </Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
              {formatCurrency(summary.tong_thu_nhap || (summary.tong_hoa_hong_truc_tiep + summary.tong_hoa_hong_gian_tiep))}
            </Typography>
            
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 2 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>Hoa hồng trực tiếp</Typography>
                <Typography variant="subtitle2" fontWeight="bold">{formatCurrency(summary.tong_hoa_hong_truc_tiep)}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>Hoa hồng gián tiếp</Typography>
                <Typography variant="subtitle2" fontWeight="bold">{formatCurrency(summary.tong_hoa_hong_gian_tiep)}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>Tổng lô đã bán</Typography>
                <Typography variant="subtitle2" fontWeight="bold">{summary.tong_so_lo} lô</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>Tổng doanh thu BĐS</Typography>
                <Typography variant="subtitle2" fontWeight="bold">{formatCurrency(summary.tong_doanh_thu)}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* --- DANH SÁCH CHI TIẾT GIAO DỊCH --- */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#333', mb: 1.5 }}>
          Chi tiết giao dịch
        </Typography>

        {list.length > 0 ? (
          list.map((item) => (
            <Card key={item._id || Math.random()} sx={{ mb: 1.5, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#1976d2' }}>
                    <MapsHomeWorkIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography variant="subtitle2" fontWeight="bold">{item.ma_nd || '---'}</Typography>
                  </Box>
                  <Chip 
                    label={item.ten_trang_thai || item.trang_thai || 'Mới tạo'} 
                    size="small" 
                    sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 22, bgcolor: item.color || '#9e9e9e', color: 'white' }} 
                  />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" noWrap>{item.ten_nguoi_mua || '---'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption">{formatDate(item.date_created)}</Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ mb: 1.5, borderStyle: 'dashed' }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <Typography variant="caption" color="text.secondary">Hoa hồng nhận được:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#388e3c' }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 18, mr: 0.5 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {formatCurrency(item.hoa_hong > 0 ? item.hoa_hong : item.hoa_hong2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body1" color="text.secondary">Chưa có giao dịch nào phù hợp.</Typography>
          </Box>
        )}

        {/* --- NÚT TẢI THÊM (LOAD MORE) --- */}
        {hasMore && list.length > 0 && (
          <Button 
            fullWidth 
            variant="text" 
            sx={{ py: 1.5, color: 'text.secondary', fontWeight: 'bold' }}
            onClick={onLoadMore}
            disabled={isFetchingMore}
          >
            {isFetchingMore ? <CircularProgress size={20} /> : 'Tải thêm'}
          </Button>
        )}

      </Box>
    );
  };

  // ================= 5. RENDER MAIN COMPONENT =================
  return (
    <Box sx={{ height: '100dvh', bgcolor: '#f4f6f8', pb: 'env(safe-area-inset-bottom)' }}>
      <ReportFrame 
        code="nhadat-hoahong-ctv"
        title="Báo cáo bán hàng"
        onBack={()=>history.goBack()}
        filterFields={filterFields}
        defaultCondition={defaultCondition}
        
        // --- BẬT TÍNH NĂNG LOAD MORE LÊN ---
        enableLoadMore={true}
        defaultLimit={20}
        
        // --- GHI ĐÈ UI BẰNG HÀM CUSTOM BÊN TRÊN ---
        renderReport={renderCustomReport}
      />
    </Box>
  );
}